import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { Loader2, Save, Palette, Code, Eye, Sparkles, Type } from 'lucide-react';
import { useBrands } from '@/lib/BrandsProvider';
import { supabase } from '@/lib/supabase';

type ColorVariable = {
  name: string;
  label: string;
  value: string;
  description: string;
};

// Popular web fonts for the font picker
const POPULAR_FONTS = [
  {
    name: 'System UI',
    value: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
    category: 'Sans Serif'
  },
  {
    name: 'Inter',
    value: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    category: 'Sans Serif'
  },
  {
    name: 'Roboto',
    value: '"Roboto", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    category: 'Sans Serif'
  },
  {
    name: 'Open Sans',
    value: '"Open Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    category: 'Sans Serif'
  },
  {
    name: 'Lato',
    value: '"Lato", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    category: 'Sans Serif'
  },
  {
    name: 'Poppins',
    value: '"Poppins", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    category: 'Sans Serif'
  },
  {
    name: 'Montserrat',
    value: '"Montserrat", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    category: 'Sans Serif'
  },
  {
    name: 'Source Sans Pro',
    value: '"Source Sans Pro", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    category: 'Sans Serif'
  },
  {
    name: 'Georgia',
    value: 'Georgia, "Times New Roman", serif',
    category: 'Serif'
  },
  {
    name: 'Times New Roman',
    value: '"Times New Roman", Georgia, serif',
    category: 'Serif'
  },
  {
    name: 'Playfair Display',
    value: '"Playfair Display", Georgia, "Times New Roman", serif',
    category: 'Serif'
  },
  {
    name: 'Merriweather',
    value: '"Merriweather", Georgia, "Times New Roman", serif',
    category: 'Serif'
  },
  {
    name: 'Crimson Text',
    value: '"Crimson Text", Georgia, "Times New Roman", serif',
    category: 'Serif'
  },
  {
    name: 'Lora',
    value: '"Lora", Georgia, "Times New Roman", serif',
    category: 'Serif'
  },
  {
    name: 'Monaco',
    value: 'Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
    category: 'Monospace'
  },
  {
    name: 'Fira Code',
    value: '"Fira Code", Monaco, "Cascadia Code", "Roboto Mono", Consolas, monospace',
    category: 'Monospace'
  },
  {
    name: 'Source Code Pro',
    value: '"Source Code Pro", Monaco, "Cascadia Code", "Roboto Mono", Consolas, monospace',
    category: 'Monospace'
  },
  {
    name: 'JetBrains Mono',
    value: '"JetBrains Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, monospace',
    category: 'Monospace'
  },
  {
    name: 'Roboto Mono',
    value: '"Roboto Mono", Monaco, "Cascadia Code", Consolas, monospace',
    category: 'Monospace'
  },
  {
    name: 'Custom',
    value: '',
    category: 'Custom'
  }
];

// Default CSS template with common variables for quick setup
const DEFAULT_CSS_TEMPLATE = `:root {
  /* Primary colors */
  --brand-primary: #3b82f6;
  --brand-primary-dark: #1d4ed8;
  --brand-primary-light: #93c5fd;
  
  /* Secondary colors */
  --brand-secondary: #10b981;
  --brand-secondary-dark: #059669;
  --brand-secondary-light: #6ee7b7;
  
  /* Accent colors */
  --brand-accent: #f97316;
  --brand-accent-dark: #ea580c;
  --brand-accent-light: #fdba74;
  
  /* Neutrals */
  --brand-neutral-100: #f3f4f6;
  --brand-neutral-200: #e5e7eb;
  --brand-neutral-300: #d1d5db;
  --brand-neutral-400: #9ca3af;
  --brand-neutral-500: #6b7280;
  --brand-neutral-600: #4b5563;
  --brand-neutral-700: #374151;
  --brand-neutral-800: #1f2937;
  --brand-neutral-900: #111827;
  
  /* Typography */
  --brand-font-primary: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  --brand-font-secondary: Georgia, 'Times New Roman', serif;
  --brand-font-monospace: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  
  /* Spacing */
  --brand-spacing-xs: 0.25rem;
  --brand-spacing-sm: 0.5rem;
  --brand-spacing-md: 1rem;
  --brand-spacing-lg: 1.5rem;
  --brand-spacing-xl: 2rem;
  --brand-spacing-2xl: 3rem;
  
  /* Border radius */
  --brand-radius-sm: 0.125rem;
  --brand-radius-md: 0.25rem;
  --brand-radius-lg: 0.5rem;
  --brand-radius-xl: 1rem;
  --brand-radius-full: 9999px;
}

/* Dark mode overrides */
.dark {
  --brand-neutral-100: #1f2937;
  --brand-neutral-200: #374151;
  --brand-neutral-300: #4b5563;
  --brand-neutral-400: #6b7280;
  --brand-neutral-500: #9ca3af;
  --brand-neutral-600: #d1d5db;
  --brand-neutral-700: #e5e7eb;
  --brand-neutral-800: #f3f4f6;
  --brand-neutral-900: #f9fafb;
}`;

// Extract color variables from CSS string
const extractVariables = (css: string): ColorVariable[] => {
  const varRegex = /--brand-([a-zA-Z0-9-]+):\s*([^;]+);/g;
  const variables: ColorVariable[] = [];
  let match;

  while ((match = varRegex.exec(css)) !== null) {
    const name = `--brand-${match[1]}`;
    const value = match[2].trim();
    
    // Create a readable label from the variable name
    let label = match[1]
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
    
    let description = '';
    
    // Add descriptions based on variable name patterns
    if (name.includes('primary')) {
      description = 'Main brand color, used for key elements';
    } else if (name.includes('secondary')) {
      description = 'Complementary color for secondary elements';
    } else if (name.includes('accent')) {
      description = 'Highlight color for call-to-action elements';
    } else if (name.includes('neutral')) {
      description = 'Neutral shade for text and backgrounds';
    } else if (name.includes('font')) {
      description = 'Typography settings for brand consistency';
    } else if (name.includes('spacing')) {
      description = 'Consistent spacing measurements';
    } else if (name.includes('radius')) {
      description = 'Border radius for rounded corners';
    }
    
    variables.push({ name, label, value, description });
  }
  
  return variables;
};

// Function to update a CSS variable value in the CSS string
const updateCssVariable = (css: string, name: string, value: string): string => {
  const regex = new RegExp(`(${name}):\\s*[^;]+;`, 'g');
  return css.replace(regex, `$1: ${value};`);
};

// Font Picker Component
const FontPicker: React.FC<{
  variable: ColorVariable;
  idx: number;
  onChange: (name: string, value: string) => void;
}> = ({ variable, idx, onChange }) => {
  const [selectedFont, setSelectedFont] = useState('');
  const [customFont, setCustomFont] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  
  const uniqueId = `${variable.name}-${idx}`;
  
  // Find the matching font in our list or mark as custom
  useEffect(() => {
    const matchingFont = POPULAR_FONTS.find(font => 
      font.value === variable.value && font.value !== ''
    );
    
    if (matchingFont) {
      setSelectedFont(matchingFont.name);
      setIsCustom(false);
    } else {
      setSelectedFont('Custom');
      setCustomFont(variable.value);
      setIsCustom(true);
    }
  }, [variable.value]);
  
  const handleFontChange = (fontName: string) => {
    setSelectedFont(fontName);
    
    if (fontName === 'Custom') {
      setIsCustom(true);
      // Don't change the value immediately, let user type
    } else {
      setIsCustom(false);
      const selectedFontObj = POPULAR_FONTS.find(f => f.name === fontName);
      if (selectedFontObj) {
        onChange(variable.name, selectedFontObj.value);
      }
    }
  };
  
  const handleCustomFontChange = (value: string) => {
    setCustomFont(value);
    onChange(variable.name, value);
  };
  
  // Group fonts by category for better organization
  const fontsByCategory = POPULAR_FONTS.reduce((acc, font) => {
    if (!acc[font.category]) acc[font.category] = [];
    acc[font.category].push(font);
    return acc;
  }, {} as Record<string, typeof POPULAR_FONTS>);
  
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="space-y-1 flex-grow">
        <div className="flex items-center gap-2">
          <Type className="w-4 h-4 text-muted-foreground" />
          <Label htmlFor={uniqueId} className="font-medium text-sm">
            {variable.label}
          </Label>
        </div>
        {variable.description && (
          <p className="text-xs text-muted-foreground">{variable.description}</p>
        )}
        {/* Font preview */}
        <div 
          className="text-sm px-2 py-1 bg-muted rounded text-muted-foreground"
          style={{ fontFamily: variable.value || 'inherit' }}
        >
          The quick brown fox jumps over the lazy dog
        </div>
      </div>
      
      <div className="flex flex-col gap-2 w-64">
        <Select value={selectedFont} onValueChange={handleFontChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select a font" />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            {Object.entries(fontsByCategory).map(([category, fonts]) => (
              <div key={category}>
                <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
                  {category}
                </div>
                {fonts.map((font) => (
                  <SelectItem key={font.name} value={font.name}>
                    <span style={{ fontFamily: font.value || 'inherit' }}>
                      {font.name}
                    </span>
                  </SelectItem>
                ))}
              </div>
            ))}
          </SelectContent>
        </Select>
        
        {isCustom && (
          <Input
            placeholder="Enter custom font stack..."
            value={customFont}
            onChange={(e) => handleCustomFontChange(e.target.value)}
            className="text-xs"
          />
        )}
      </div>
    </div>
  );
};

const BrandTheme: React.FC = () => {
  const { selectedBrand } = useBrands();
  const [loading, setLoading] = useState(true);
  const [cssValue, setCssValue] = useState('');
  const [variables, setVariables] = useState<ColorVariable[]>([]);
  const [activeTab, setActiveTab] = useState('visual');
  const [isSaving, setIsSaving] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [generatingCss, setGeneratingCss] = useState(false);
  
  // Load CSS when brand changes
  useEffect(() => {
    const loadBrandCss = async () => {
      if (!selectedBrand) return;
      
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('brands')
          .select('brand_css')
          .eq('id', selectedBrand.id)
          .single();
          
        if (error) throw error;
        
        if (data && data.brand_css) {
          setCssValue(data.brand_css);
          setVariables(extractVariables(data.brand_css));
        } else {
          // If no CSS exists, use the default template
          setCssValue(DEFAULT_CSS_TEMPLATE);
          setVariables(extractVariables(DEFAULT_CSS_TEMPLATE));
        }
      } catch (error) {
        console.error('Error loading brand CSS:', error);
        toast({
          title: 'Error',
          description: 'Failed to load brand theme CSS',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadBrandCss();
  }, [selectedBrand]);
  
  // Update variables when CSS code changes
  useEffect(() => {
    setVariables(extractVariables(cssValue));
  }, [cssValue]);
  
  // Handle CSS variable changes in visual editor
  const handleVariableChange = (name: string, value: string) => {
    const updatedCss = updateCssVariable(cssValue, name, value);
    setCssValue(updatedCss);
    
    // Update the variables state to reflect the change
    setVariables(prev => 
      prev.map(v => v.name === name ? { ...v, value } : v)
    );
  };
  
  // Save CSS to the brand
  const saveCss = async () => {
    if (!selectedBrand) return;
    
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('brands')
        .update({ brand_css: cssValue })
        .eq('id', selectedBrand.id);
        
      if (error) throw error;
      
      toast({
        title: 'Theme Saved',
        description: 'Brand theme CSS has been updated successfully',
        variant: 'default',
        className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
      });
    } catch (error) {
      console.error('Error saving brand CSS:', error);
      toast({
        title: 'Error',
        description: 'Failed to save brand theme CSS',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  // Generate CSS with AI
  const generateCssWithAI = async () => {
    if (!selectedBrand) return;
    
    setGeneratingCss(true);
    try {
      // Generate a prompt based on brand information
      const prompt = `
        Create CSS custom properties (variables) for a brand theme.
        
        Brand Name: ${selectedBrand.brand_name}
        
        ${selectedBrand.brand_values ? `Brand Values: ${selectedBrand.brand_values}` : ''}
        ${selectedBrand.brand_voice ? `Brand Voice: ${selectedBrand.brand_voice}` : ''}
        
        Generate CSS variables for:
        - Primary colors (main brand color)
        - Secondary colors
        - Accent/tertiary colors
        - Neutral colors (grayscale)
        - Typography (font families)
        - Spacing values
        - Border radius values
        
        Format it as CSS custom properties with :root {} and include comments.
      `;

      console.log('Prompt:', prompt);
      
      // For now, just use the default template since we can't make AI calls here
      // In a real implementation, this would make an API call to an AI service
      // This is just a placeholder to show the flow
      setTimeout(() => {
        setCssValue(DEFAULT_CSS_TEMPLATE);
        setVariables(extractVariables(DEFAULT_CSS_TEMPLATE));
        
        toast({
          title: 'CSS Generated',
          description: 'Brand theme CSS has been generated with AI',
          variant: 'default',
          className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
        });
        setGeneratingCss(false);
      }, 1500);
    } catch (error) {
      console.error('Error generating CSS with AI:', error);
      toast({
        title: 'Generation Failed',
        description: error instanceof Error ? error.message : 'Failed to generate CSS with AI',
        variant: 'destructive',
      });
      setGeneratingCss(false);
    }
  };
  
  // Reset to default template
  const resetToDefault = () => {
    setCssValue(DEFAULT_CSS_TEMPLATE);
    setVariables(extractVariables(DEFAULT_CSS_TEMPLATE));
    
    toast({
      title: 'Reset Complete',
      description: 'Brand theme has been reset to default template',
      variant: 'default',
    });
  };
  
  // Group variables by category for the visual editor
  const groupedVariables = variables.reduce((groups, variable) => {
    let category = 'Other';
    
    if (variable.name.includes('primary')) category = 'Primary Colors';
    else if (variable.name.includes('secondary')) category = 'Secondary Colors';
    else if (variable.name.includes('accent')) category = 'Accent Colors';
    else if (variable.name.includes('neutral')) category = 'Neutral Colors';
    else if (variable.name.includes('font')) category = 'Typography';
    else if (variable.name.includes('spacing')) category = 'Spacing';
    else if (variable.name.includes('radius')) category = 'Border Radius';
    
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(variable);
    return groups;
  }, {} as Record<string, ColorVariable[]>);
  
  // Function to render color variables
  const renderColorVariable = (variable: ColorVariable, idx: number = 0) => {
    const colorNames = ['primary', 'secondary', 'accent', 'neutral', 'success', 'error', 'warning'];
    const isColor = colorNames.some(c => variable.name.includes(c)) &&
      !variable.name.includes('font') && !variable.name.includes('spacing') && !variable.name.includes('radius');
    
    const isFont = variable.name.includes('font');
    const uniqueId = `${variable.name}-${idx}`;
    
    // Use FontPicker for font variables
    if (isFont) {
      return (
        <FontPicker 
          key={`${variable.name}-${idx}`}
          variable={variable} 
          idx={idx} 
          onChange={handleVariableChange} 
        />
      );
    }
    
    if (isColor && variable.value.trim().startsWith('#')) {
      return (
        <div className="flex items-center gap-3 mb-4">
          <div className="space-y-1 flex-grow">
            <div className="flex items-center gap-2">
              <div 
                className="w-5 h-5 rounded-full border"
                style={{ backgroundColor: variable.value }}
              />
              <Label htmlFor={uniqueId} className="font-medium text-sm">
                {variable.label}
              </Label>
            </div>
            {variable.description && (
              <p className="text-xs text-muted-foreground">{variable.description}</p>
            )}
          </div>
          <Input
            id={uniqueId}
            type="color"
            value={variable.value}
            onChange={(e) => handleVariableChange(variable.name, e.target.value)}
            className="w-14 h-8 p-1"
          />
          <Input
            type="text"
            value={variable.value}
            onChange={(e) => handleVariableChange(variable.name, e.target.value)}
            className="w-24 h-8"
          />
        </div>
      );
    }
    
    // For non-color, non-font variables
    return (
      <div className="flex items-center gap-3 mb-4">
        <div className="space-y-1 flex-grow">
          <Label htmlFor={uniqueId} className="font-medium text-sm">
            {variable.label}
          </Label>
          {variable.description && (
            <p className="text-xs text-muted-foreground">{variable.description}</p>
          )}
        </div>
        <Input
          id={uniqueId}
          type="text"
          value={variable.value}
          onChange={(e) => handleVariableChange(variable.name, e.target.value)}
          className="w-48 h-8"
        />
      </div>
    );
  };
  
  // Render the CSS preview with the current theme
  const renderPreview = () => {
    return (
      <div className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Theme Preview</CardTitle>
            <CardDescription>
              Preview how your theme variables will look in the UI
            </CardDescription>
          </CardHeader>
          <CardContent>
            <style>
              {cssValue}
            </style>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h3 className="text-sm font-medium">Primary Colors</h3>
                  <div className="flex space-x-2">
                    <div key="primary" className="w-10 h-10 rounded" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                    <div key="primary-dark" className="w-10 h-10 rounded" style={{ backgroundColor: 'var(--brand-primary-dark)' }}></div>
                    <div key="primary-light" className="w-10 h-10 rounded" style={{ backgroundColor: 'var(--brand-primary-light)' }}></div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-sm font-medium">Secondary Colors</h3>
                  <div className="flex space-x-2">
                    <div key="secondary" className="w-10 h-10 rounded" style={{ backgroundColor: 'var(--brand-secondary)' }}></div>
                    <div key="secondary-dark" className="w-10 h-10 rounded" style={{ backgroundColor: 'var(--brand-secondary-dark)' }}></div>
                    <div key="secondary-light" className="w-10 h-10 rounded" style={{ backgroundColor: 'var(--brand-secondary-light)' }}></div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Accent Colors</h3>
                <div className="flex space-x-2">
                  <div key="accent" className="w-10 h-10 rounded" style={{ backgroundColor: 'var(--brand-accent)' }}></div>
                  <div key="accent-dark" className="w-10 h-10 rounded" style={{ backgroundColor: 'var(--brand-accent-dark)' }}></div>
                  <div key="accent-light" className="w-10 h-10 rounded" style={{ backgroundColor: 'var(--brand-accent-light)' }}></div>
                </div>
              </div>
              
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Neutral Colors</h3>
                <div className="flex space-x-2 overflow-x-auto pb-2">
                  <div key="neutral-100" className="w-10 h-10 rounded" style={{ backgroundColor: 'var(--brand-neutral-100)' }}></div>
                  <div key="neutral-200" className="w-10 h-10 rounded" style={{ backgroundColor: 'var(--brand-neutral-200)' }}></div>
                  <div key="neutral-300" className="w-10 h-10 rounded" style={{ backgroundColor: 'var(--brand-neutral-300)' }}></div>
                  <div key="neutral-400" className="w-10 h-10 rounded" style={{ backgroundColor: 'var(--brand-neutral-400)' }}></div>
                  <div key="neutral-500" className="w-10 h-10 rounded" style={{ backgroundColor: 'var(--brand-neutral-500)' }}></div>
                  <div key="neutral-600" className="w-10 h-10 rounded" style={{ backgroundColor: 'var(--brand-neutral-600)' }}></div>
                  <div key="neutral-700" className="w-10 h-10 rounded" style={{ backgroundColor: 'var(--brand-neutral-700)' }}></div>
                  <div key="neutral-800" className="w-10 h-10 rounded" style={{ backgroundColor: 'var(--brand-neutral-800)' }}></div>
                  <div key="neutral-900" className="w-10 h-10 rounded" style={{ backgroundColor: 'var(--brand-neutral-900)' }}></div>
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-sm font-medium">UI Components with Theme</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-xs font-medium mb-2">Buttons</h4>
                    <div className="flex flex-wrap gap-2">
                      <button className="px-4 py-2 rounded" style={{ 
                        backgroundColor: 'var(--brand-primary)', 
                        color: 'white',
                        borderRadius: 'var(--brand-radius-md)'
                      }}>
                        Primary Button
                      </button>
                      
                      <button className="px-4 py-2 rounded" style={{ 
                        backgroundColor: 'var(--brand-secondary)', 
                        color: 'white',
                        borderRadius: 'var(--brand-radius-md)'
                      }}>
                        Secondary Button
                      </button>
                      
                      <button className="px-4 py-2 rounded" style={{ 
                        backgroundColor: 'var(--brand-accent)', 
                        color: 'white',
                        borderRadius: 'var(--brand-radius-md)'
                      }}>
                        Accent Button
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-xs font-medium mb-2">Cards</h4>
                    <div className="p-4 rounded" style={{ 
                      backgroundColor: 'var(--brand-neutral-100)',
                      borderRadius: 'var(--brand-radius-lg)',
                      border: '1px solid var(--brand-neutral-200)'
                    }}>
                      <div className="text-sm font-medium" style={{ color: 'var(--brand-neutral-900)' }}>Card Title</div>
                      <p className="text-xs mt-1" style={{ color: 'var(--brand-neutral-600)' }}>This is a card component styled with your theme variables.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Typography</h3>
                <div className="space-y-2 p-4 rounded" style={{ backgroundColor: 'var(--brand-neutral-100)' }}>
                  <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--brand-font-primary)' }}>
                    Primary Heading
                  </h1>
                  <h2 className="text-xl" style={{ fontFamily: 'var(--brand-font-secondary)' }}>
                    Secondary Heading
                  </h2>
                  <p className="text-sm" style={{ 
                    fontFamily: 'var(--brand-font-primary)',
                    color: 'var(--brand-neutral-700)' 
                  }}>
                    This is a paragraph with primary font. The quick brown fox jumps over the lazy dog.
                  </p>
                  <code className="text-sm p-1 rounded" style={{ 
                    fontFamily: 'var(--brand-font-monospace)',
                    backgroundColor: 'var(--brand-neutral-200)' 
                  }}>
                    Code example in monospace font
                  </code>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };
  
  return (
    <div className="container mx-auto p-6 space-y-6">
      
      <div className="flex justify-between items-center">
        <Tabs 
          value={activeTab} 
          onValueChange={setActiveTab}
          className="w-full"
        >
          <div className="flex justify-between items-center mb-4">
            <TabsList>
              <TabsTrigger value="visual">
                <Palette className="h-4 w-4 mr-2" />
                Visual Editor
              </TabsTrigger>
              <TabsTrigger value="code">
                <Code className="h-4 w-4 mr-2" />
                Code Editor
              </TabsTrigger>
            </TabsList>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreviewVisible(!previewVisible)}
              >
                <Eye className="h-4 w-4 mr-2" />
                {previewVisible ? 'Hide Preview' : 'Show Preview'}
              </Button>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={resetToDefault}
              >
                Reset to Default
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={generateCssWithAI}
                disabled={generatingCss}
              >
                {generatingCss ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate with AI
                  </>
                )}
              </Button>
              
              <Button
                onClick={saveCss}
                disabled={isSaving}
                size="sm"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Theme
                  </>
                )}
              </Button>
            </div>
          </div>
          
          <TabsContent value="visual" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Visual Theme Editor</CardTitle>
                <CardDescription>
                  Customize your brand's CSS variables using this visual editor
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div>
                    {Object.entries(groupedVariables).map(([category, vars]) => (
                      <div key={category} className="mb-6">
                        <h3 className="text-lg font-medium mb-3">{category}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                          {vars.map((variable, index) => (
                            <React.Fragment key={`${variable.name}-${index}`}>
                              {renderColorVariable(variable, index)}
                            </React.Fragment>
                          ))}
                        </div>
                        <Separator className="mt-4" />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
              <CardFooter className="justify-between flex-col sm:flex-row items-start sm:items-center gap-2">
                <p className="text-sm text-muted-foreground">
                  Changes are applied immediately in the preview, but you must click "Save Theme" to persist them.
                </p>
                <Button
                  onClick={saveCss}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Theme
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="code" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>CSS Code Editor</CardTitle>
                <CardDescription>
                  Edit the raw CSS for your brand theme
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <Textarea 
                    value={cssValue} 
                    onChange={(e) => setCssValue(e.target.value)}
                    className="font-mono text-sm min-h-[350px]"
                  />
                )}
              </CardContent>
              <CardFooter className="justify-between flex-col sm:flex-row items-start sm:items-center gap-2">
                <p className="text-sm text-muted-foreground">
                  Use CSS custom properties (variables) with the --brand- prefix for best results.
                </p>
                <Button
                  onClick={saveCss}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Theme
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {previewVisible && renderPreview()}
    </div>
  );
};

export default BrandTheme;